/**
 * Country dial codes for phone input (country selector).
 * Format: E.164 prefix (e.g. +213). Sorted by dial digits length descending for parsing.
 */
export interface CountryDialCode {
  code: string;
  name: string;
  dial: string;
  /** Digits only (e.g. "213") for matching. */
  dialDigits: string;
}

export const COUNTRY_DIAL_CODES: CountryDialCode[] = [
  { code: "DZ", name: "Algeria", dial: "+213", dialDigits: "213" },
  { code: "FR", name: "France", dial: "+33", dialDigits: "33" },
  { code: "MA", name: "Morocco", dial: "+212", dialDigits: "212" },
  { code: "TN", name: "Tunisia", dial: "+216", dialDigits: "216" },
  { code: "GB", name: "United Kingdom", dial: "+44", dialDigits: "44" },
  { code: "US", name: "United States", dial: "+1", dialDigits: "1" },
  { code: "CA", name: "Canada", dial: "+1", dialDigits: "1" },
  { code: "DE", name: "Germany", dial: "+49", dialDigits: "49" },
  { code: "ES", name: "Spain", dial: "+34", dialDigits: "34" },
  { code: "IT", name: "Italy", dial: "+39", dialDigits: "39" },
  { code: "BE", name: "Belgium", dial: "+32", dialDigits: "32" },
  { code: "CH", name: "Switzerland", dial: "+41", dialDigits: "41" },
  { code: "NL", name: "Netherlands", dial: "+31", dialDigits: "31" },
  { code: "EG", name: "Egypt", dial: "+20", dialDigits: "20" },
  { code: "SA", name: "Saudi Arabia", dial: "+966", dialDigits: "966" },
  { code: "AE", name: "United Arab Emirates", dial: "+971", dialDigits: "971" },
  { code: "CN", name: "China", dial: "+86", dialDigits: "86" },
  { code: "IN", name: "India", dial: "+91", dialDigits: "91" },
  { code: "TR", name: "Turkey", dial: "+90", dialDigits: "90" },
  { code: "RU", name: "Russia", dial: "+7", dialDigits: "7" },
  { code: "BR", name: "Brazil", dial: "+55", dialDigits: "55" },
  { code: "JP", name: "Japan", dial: "+81", dialDigits: "81" },
  { code: "AU", name: "Australia", dial: "+61", dialDigits: "61" },
  { code: "ZA", name: "South Africa", dial: "+27", dialDigits: "27" },
  { code: "PL", name: "Poland", dial: "+48", dialDigits: "48" },
  { code: "PT", name: "Portugal", dial: "+351", dialDigits: "351" },
  { code: "GR", name: "Greece", dial: "+30", dialDigits: "30" },
  { code: "SE", name: "Sweden", dial: "+46", dialDigits: "46" },
  { code: "NO", name: "Norway", dial: "+47", dialDigits: "47" },
  { code: "DK", name: "Denmark", dial: "+45", dialDigits: "45" },
  { code: "FI", name: "Finland", dial: "+358", dialDigits: "358" },
  { code: "IE", name: "Ireland", dial: "+353", dialDigits: "353" },
  { code: "AT", name: "Austria", dial: "+43", dialDigits: "43" },
  { code: "LU", name: "Luxembourg", dial: "+352", dialDigits: "352" },
  { code: "LY", name: "Libya", dial: "+218", dialDigits: "218" },
  { code: "MR", name: "Mauritania", dial: "+222", dialDigits: "222" },
  { code: "ML", name: "Mali", dial: "+223", dialDigits: "223" },
  { code: "SN", name: "Senegal", dial: "+221", dialDigits: "221" },
  { code: "NE", name: "Niger", dial: "+227", dialDigits: "227" },
  { code: "TD", name: "Chad", dial: "+235", dialDigits: "235" },
  { code: "MX", name: "Mexico", dial: "+52", dialDigits: "52" },
  { code: "AR", name: "Argentina", dial: "+54", dialDigits: "54" },
  { code: "CL", name: "Chile", dial: "+56", dialDigits: "56" },
  { code: "CO", name: "Colombia", dial: "+57", dialDigits: "57" },
  { code: "NG", name: "Nigeria", dial: "+234", dialDigits: "234" },
  { code: "KE", name: "Kenya", dial: "+254", dialDigits: "254" },
  { code: "PK", name: "Pakistan", dial: "+92", dialDigits: "92" },
  { code: "BD", name: "Bangladesh", dial: "+880", dialDigits: "880" },
  { code: "ID", name: "Indonesia", dial: "+62", dialDigits: "62" },
  { code: "MY", name: "Malaysia", dial: "+60", dialDigits: "60" },
  { code: "TH", name: "Thailand", dial: "+66", dialDigits: "66" },
  { code: "VN", name: "Vietnam", dial: "+84", dialDigits: "84" },
  { code: "PH", name: "Philippines", dial: "+63", dialDigits: "63" },
  { code: "IL", name: "Israel", dial: "+972", dialDigits: "972" },
  { code: "LB", name: "Lebanon", dial: "+961", dialDigits: "961" },
  { code: "JO", name: "Jordan", dial: "+962", dialDigits: "962" },
  { code: "KW", name: "Kuwait", dial: "+965", dialDigits: "965" },
  { code: "QA", name: "Qatar", dial: "+974", dialDigits: "974" },
  { code: "BH", name: "Bahrain", dial: "+973", dialDigits: "973" },
  { code: "OM", name: "Oman", dial: "+968", dialDigits: "968" },
].sort((a, b) => b.dialDigits.length - a.dialDigits.length);

/** Get country by dial code digits (e.g. "213" -> Algeria). */
export function getCountryByDialDigits(digits: string): CountryDialCode | undefined {
  const normalized = digits.replace(/\D/g, "");
  for (const c of COUNTRY_DIAL_CODES) {
    if (normalized === c.dialDigits || normalized.startsWith(c.dialDigits)) {
      return c;
    }
  }
  return undefined;
}

/** Normalize digits: 00213 -> 213 (international prefix). */
function normalizeDigits(digits: string): string {
  if (digits.startsWith("00") && digits.length > 2) {
    return digits.slice(2);
  }
  return digits;
}

/** Parse full phone string into country and national number. Uses longest matching dial code. */
export function parsePhoneValue(value: string | null | undefined): {
  country: CountryDialCode;
  nationalNumber: string;
} {
  const str = (value ?? "").trim();
  let digitsOnly = str.replace(/\D/g, "");
  digitsOnly = normalizeDigits(digitsOnly);
  if (!digitsOnly) {
    return { country: COUNTRY_DIAL_CODES[0], nationalNumber: "" };
  }
  for (const c of COUNTRY_DIAL_CODES) {
    if (digitsOnly.length >= c.dialDigits.length && digitsOnly.startsWith(c.dialDigits)) {
      const national = digitsOnly.slice(c.dialDigits.length);
      return { country: c, nationalNumber: national };
    }
  }
  return { country: COUNTRY_DIAL_CODES[0], nationalNumber: digitsOnly };
}

/** Format to stored value: E.164 style without spaces (e.g. "+213555123456"). */
export function formatPhoneStored(dial: string, nationalNumber: string): string {
  const digits = nationalNumber.replace(/\D/g, "");
  if (!digits) return dial;
  return `${dial}${digits}`;
}
